import React, { useState } from 'react'
import {
  Paper,
} from '@mui/material'
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers'
import moment, { Moment } from 'moment/moment'
import _data from 'common/utils/_data'
import { useGetAvailability } from 'common/hooks/useGetAvailability'
import { MemberProfileExtendedType } from 'common/types/MemberProfile'
import { ModalFooter } from '../../../../../../common/components/wizard/ModalFooter'
import * as Yup from 'yup'
import { SimpleErrorMessage } from '../common/SimpleErrorMessage'
import { BookingCategory, BookingPayload, BookingPayloadBase, BookingPayloadLegalAdvice, BookingDetailsFields } from 'common/types/MemberBooking'
import { usePostBooking } from './usePostBooking'
import { buildBasePayload, buildDocumentReviewPayload, buildLegalAdvicePayload } from 'cwp-components/CWP Sections/MemberProfile/MemberBookingWizard/helpers'

interface Step3BookingCalendarProps {
  bookingCategory: BookingCategory
  profile: MemberProfileExtendedType
  bookingDateTime: Date | null
  setBookingDateTime: (date: Date) => void
  bookingDetails: BookingDetailsFields
  selectedFile?: File | null
}

export const Step3BookingCalendar = ({
  bookingCategory,
  profile,
  bookingDateTime,
  setBookingDateTime,
  bookingDetails,
  selectedFile
}: Step3BookingCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTime, setSelectedTime] = useState<string>('')
  const { slots, fetchSlots } = useGetAvailability(bookingCategory)
  const [error, setError] = useState<Yup.ValidationError | null>(null)
  const bookingCategoryText =
    bookingCategory === BookingCategory.DOCUMENT_REVIEW
      ? 'deadline delivery'
      : 'appointment'
  const { postBooking } = usePostBooking()

  const handleDateChange = (newValue: Moment | null) => {
    if (newValue) {
      setError(null)
      setBookingDateTime(null)
      setSelectedTime('')
      setSelectedDate(newValue.toDate())
      fetchSlots(newValue.toDate(), profile?.metadata.id)
    }
  }

  const handleTimeSelection = (slot: { start: string }) => {
    setError(null)
    setSelectedTime(slot.start)
    const combinedDateTime = moment(selectedDate)
      .set({
        hour: moment(slot.start, 'HH:mm').hour(),
        minute: moment(slot.start, 'HH:mm').minute(),
      })
      .utc()
      .toDate()
    setBookingDateTime(combinedDateTime)
  }

  const validationSchema = Yup.object().shape({
    bookingDateTime: Yup.date()
      .required('Please select a date and time')
  })

  const doValidation = async () => {
    try {
      await validationSchema.validate({ bookingDateTime })
      setError(null)
      return await sendToApi()
    } catch (error) {
      setError(error)
      return false
    }
  }

  const sendToApi = async () => {
    try {
      const basePayload = buildBasePayload(profile, bookingDetails, bookingCategory)

      let payload: BookingPayload
      switch (bookingCategory) {
        case BookingCategory.DOCUMENT_REVIEW: {
          payload = buildDocumentReviewPayload(basePayload, bookingDateTime, bookingDetails, selectedFile)
          console.log('Document Review Payload:', payload)
          break
        }
        case BookingCategory.LEGAL_ADVICE: {
          payload = buildLegalAdvicePayload(basePayload, bookingDateTime, bookingDetails)
          break
        }
        default: {
          throw new Error('Unsupported booking category.')
        }
      }

      await postBooking(payload)
      return true
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setError(error)
      } else {
        const formError = new Yup.ValidationError(
          'Sorry but something failed on our server. Please contact support.',
          null,
          'form'
        )
        setError(formError)
      }
      return false
    }
  }

  // Disable dates that are NOT in the future, including today
  const shouldDisableDate = (date: Moment) => {
    return date.isSameOrBefore(moment(), 'day')
  }

  return (
    <>
      <div className='grid grid-cols-[1fr_2px_1fr] gap-8'>
        <div className=''>
          <h5 className='text-[#37455C] font-medium text-[16px] tracking-[0.4px] mb-4'>
            Select {bookingCategoryText} day:
          </h5>
          <LocalizationProvider>
            <Paper elevation={3} className='rounded-2xl w-fit h-fit'>
              <StaticDatePicker
                sx={{
                  '.MuiPickersLayout-contentWrapper': {
                    height: '340px',
                  },
                }}
                value={selectedDate ? moment(selectedDate) : null}
                onChange={handleDateChange}
                shouldDisableDate={shouldDisableDate}
                minDate={moment()}
                maxDate={moment().add(3, 'years')}
                slots={{
                  toolbar: () => null,
                  actionBar: () => null,
                }}
              />
            </Paper>
          </LocalizationProvider>
        </div>
        <div className='border-l-2 border-dashed border-[#9fcdd7]'></div>
        {selectedDate ? (
          <div className=''>
            <h5 className='text-[#37455C] font-medium text-[16px] tracking-[0.4px] mb-4'>
              Select {bookingCategoryText} time:
            </h5>
            <div className='flex flex-wrap gap-3'>
              {slots.map((slot) => (
                <button
                  key={slot.start}
                  onClick={() => handleTimeSelection(slot)}
                  className={`rounded-md border border-solid border-[#d9e0df] w-[97px] cursor-pointer ${selectedTime === slot.start
                    ? 'border-0 bg-[#BBD2D7]'
                    : 'hover:border-0 bg-white hover:bg-[#F4F4F4] hover:text-[#0C6156]'
                    } text-black p-2`}
                >
                  {moment(slot.start, 'HH:mm').format('hh:mm A').toLowerCase()}{' '}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex align-middle items-center justify-center">
            <h5 className='text-[#37455C] font-medium text-center text-[16px] tracking-[0.4px] align-center mb-4'>
              Provisional times will appear here for the day you select.
            </h5>
          </div>
        )}
      </div>
      {error && <SimpleErrorMessage message={error.message} />}
      <ModalFooter doBeforeNextStep={doValidation} forwardButtonText='Send' />
    </>
  )
}
